import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Layout } from '@/components/layout/Layout';
import { toast } from 'sonner';
import axios from 'axios';
import { ChefHat, Globe, Languages, Flame, Check, Upload, FileText, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChefApplicationPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience_level: '',
    pizza_types: [],
    oven_types: [],
    languages: [],
    available_countries: [],
    current_country: '',
    bio: '',
    cv_url: ''
  });

  const experienceLevels = [
    { value: '0-1', label: t.experienceLevels['0-1'] },
    { value: '1-2', label: t.experienceLevels['1-2'] },
    { value: '2-5', label: t.experienceLevels['2-5'] },
    { value: '5-10', label: t.experienceLevels['5-10'] },
    { value: '10+', label: t.experienceLevels['10+'] }
  ];

  const pizzaTypes = [
    { value: 'napoletana', label: t.pizzaTypes.napoletana },
    { value: 'romana', label: t.pizzaTypes.romana },
    { value: 'contemporanea', label: t.pizzaTypes.contemporanea },
    { value: 'teglia', label: t.pizzaTypes.teglia },
    { value: 'pinsa', label: t.pizzaTypes.pinsa }
  ];

  const ovenTypes = [
    { value: 'legna', label: t.ovenTypes.legna },
    { value: 'gas', label: t.ovenTypes.gas },
    { value: 'elettrico', label: t.ovenTypes.elettrico },
    { value: 'misto', label: t.ovenTypes.misto }
  ];

  const languageOptions = [
    { value: 'italian', label: t.languages.italian },
    { value: 'english', label: t.languages.english },
    { value: 'french', label: t.languages.french },
    { value: 'spanish', label: t.languages.spanish },
    { value: 'german', label: t.languages.german },
    { value: 'portuguese', label: t.languages.portuguese }
  ];

  const countries = [
    { value: 'all_europe', label: t.countries.all_europe },
    { value: 'italy', label: t.countries.italy },
    { value: 'france', label: t.countries.france },
    { value: 'germany', label: t.countries.germany },
    { value: 'switzerland', label: t.countries.switzerland },
    { value: 'spain', label: t.countries.spain },
    { value: 'uk', label: t.countries.uk },
    { value: 'netherlands', label: t.countries.netherlands },
    { value: 'belgium', label: t.countries.belgium },
    { value: 'austria', label: t.countries.austria },
    { value: 'sweden', label: t.countries.sweden }
  ];

  const handleMultiSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato non supportato. Usa PDF, DOC o DOCX.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 5MB.');
      return;
    }

    setCvUploading(true);
    setCvFile(file);

    // Convert to base64 for storage
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        cv_url: reader.result
      }));
      setCvUploading(false);
      toast.success(t.chefForm.cvUploaded);
    };
    reader.onerror = () => {
      setCvUploading(false);
      toast.error('Errore durante il caricamento');
    };
    reader.readAsDataURL(file);
  };

  const removeCv = () => {
    setCvFile(null);
    setFormData(prev => ({ ...prev, cv_url: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/chefs/apply`, formData);
      setSubmitted(true);
      toast.success(t.chefForm.success);
    } catch (error) {
      const message = error.response?.data?.detail || t.chefForm.error;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="submission-success">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Check className="text-primary" size={40} />
            </div>
            <h1 className="text-3xl font-serif text-foreground mb-4">
              {t.chefForm.success}
            </h1>
            <p className="text-muted-foreground">
              Ti contatteremo presto per discutere le opportunità disponibili.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-12 md:py-24 relative min-h-screen">
        <div 
          className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url("https://static.prod-images.emergentagent.com/jobs/d9cf0590-fa7d-4aa8-865e-545c02b78803/images/e2da5b85985bc0abe3e871a0371e66b45db3d3aaafda4cab05bab59285bdd1e9.png")' }}
        />
        <div className="fixed inset-0 z-0 bg-black/70" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <ChefHat className="text-primary" size={32} />
            </div>
            <h1 className="text-3xl md:text-5xl font-serif text-foreground mb-4" data-testid="chef-form-title">
              {t.chefForm.title}
            </h1>
            <p className="text-muted-foreground text-lg">
              {t.chefForm.subtitle}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="chef-application-form">
            {/* Personal Info */}
            <div className="bg-card border border-primary/10 rounded-sm p-6 md:p-8">
              <h2 className="text-xl font-serif text-foreground mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm">1</span>
                {t.chefForm.section1}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.chefForm.name} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="form-input w-full"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.chefForm.email} *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input w-full"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.chefForm.phone} *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="form-input w-full"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t.chefForm.currentCountry} *
                  </label>
                  <select
                    required
                    value={formData.current_country}
                    onChange={(e) => setFormData({ ...formData, current_country: e.target.value })}
                    className="form-select w-full"
                    data-testid="select-current-country"
                  >
                    <option value="">Seleziona...</option>
                    {countries.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Experience */}
            <div className="bg-card border border-primary/10 rounded-sm p-6 md:p-8">
              <h2 className="text-xl font-serif text-foreground mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm">2</span>
                {t.chefForm.section2}
              </h2>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-3">
                  {t.chefForm.experience} *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {experienceLevels.map(level => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, experience_level: level.value })}
                      className={`p-3 text-sm rounded-sm border transition-all duration-200 ${
                        formData.experience_level === level.value
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-primary/20 text-foreground hover:border-primary/40'
                      }`}
                      data-testid={`exp-${level.value}`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Flame size={16} className="text-primary" />
                  {t.chefForm.pizzaTypes} *
                </label>
                <div className="flex flex-wrap gap-3">
                  {pizzaTypes.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleMultiSelect('pizza_types', type.value)}
                      className={`px-4 py-2 text-sm rounded-sm border transition-all duration-200 ${
                        formData.pizza_types.includes(type.value)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-primary/20 text-foreground hover:border-primary/40'
                      }`}
                      data-testid={`pizza-${type.value}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  {t.chefForm.ovenTypes} *
                </label>
                <div className="flex flex-wrap gap-3">
                  {ovenTypes.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleMultiSelect('oven_types', type.value)}
                      className={`px-4 py-2 text-sm rounded-sm border transition-all duration-200 ${
                        formData.oven_types.includes(type.value)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-primary/20 text-foreground hover:border-primary/40'
                      }`}
                      data-testid={`oven-${type.value}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Languages & Availability */}
            <div className="bg-card border border-primary/10 rounded-sm p-6 md:p-8">
              <h2 className="text-xl font-serif text-foreground mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm">3</span>
                {t.chefForm.section3}
              </h2>

              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Languages size={16} className="text-primary" />
                  {t.chefForm.languages} *
                </label>
                <div className="flex flex-wrap gap-3">
                  {languageOptions.map(lang => (
                    <button
                      key={lang.value}
                      type="button"
                      onClick={() => handleMultiSelect('languages', lang.value)}
                      className={`px-4 py-2 text-sm rounded-sm border transition-all duration-200 ${
                        formData.languages.includes(lang.value)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-primary/20 text-foreground hover:border-primary/40'
                      }`}
                      data-testid={`lang-${lang.value}`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Globe size={16} className="text-primary" />
                  {t.chefForm.countries} *
                </label>
                <div className="flex flex-wrap gap-3">
                  {countries.map(country => (
                    <button
                      key={country.value}
                      type="button"
                      onClick={() => handleMultiSelect('available_countries', country.value)}
                      className={`px-4 py-2 text-sm rounded-sm border transition-all duration-200 ${
                        formData.available_countries.includes(country.value)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-primary/20 text-foreground hover:border-primary/40'
                      }`}
                      data-testid={`country-${country.value}`}
                    >
                      {country.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Bio & CV */}
            <div className="bg-card border border-primary/10 rounded-sm p-6 md:p-8">
              <h2 className="text-xl font-serif text-foreground mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm">4</span>
                {t.chefForm.section4}
              </h2>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t.chefForm.bio} *
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder={t.chefForm.bioPlaceholder}
                  className="form-input w-full resize-none"
                  data-testid="input-bio"
                />
              </div>

              {/* CV Upload */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t.chefForm.cv}
                </label>
                
                {!cvFile ? (
                  <label 
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-primary/30 rounded-sm cursor-pointer hover:border-primary/50 transition-colors bg-muted/20"
                    data-testid="cv-upload-area"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-primary/60" />
                      <p className="text-sm text-foreground font-medium">{t.chefForm.cvUpload}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t.chefForm.cvFormats}</p>
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleCvUpload}
                      data-testid="cv-file-input"
                    />
                  </label>
                ) : (
                  <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/20 rounded-sm">
                    <div className="flex items-center gap-3">
                      <FileText className="text-primary" size={24} />
                      <div>
                        <p className="text-sm font-medium text-foreground">{cvFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(cvFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeCv}
                      className="p-2 text-red-400 hover:text-red-300 transition-colors"
                      data-testid="remove-cv-btn"
                    >
                      <X size={20} />
                    </button>
                  </div>
                )}
                
                {cvUploading && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Caricamento...
                  </div>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer justify-center">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="w-5 h-5 rounded-sm border-primary/30 bg-muted text-primary focus:ring-primary"
                  data-testid="checkbox-consent"
                />
                <span className="text-foreground text-sm">
                  Accetto la <a href="/privacy-policy" target="_blank" className="text-primary hover:underline">Privacy Policy</a> e i <a href="/termini-servizio" target="_blank" className="text-primary hover:underline">Termini di Servizio</a>
                </span>
              </label>
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={loading || !consent || !formData.experience_level || formData.pizza_types.length === 0 || formData.oven_types.length === 0 || formData.languages.length === 0 || formData.available_countries.length === 0}
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold uppercase tracking-widest px-12 py-4 rounded-sm btn-glow hover:brightness-110 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-chef-form"
              >
                {loading ? 'Invio in corso...' : t.chefForm.submit}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
